import React, { useMemo, useState } from 'react';
import {
  Container,
  Card,
  Row as BootstrapRow,
  Col as BootstrapCol,
  Button,
  Modal as BootstrapModal,
  Form as BootstrapForm,
} from 'react-bootstrap';
import { Table, Select, DatePicker, Form, Tag, App } from 'antd';
import { FilterOutlined, PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import FormItem from '@/common/FormItem/FormItem';
import LifecycleRulesDrawer, {
  LifecycleRulesInfoButton,
} from '@/common/LifecycleRulesDrawer/LifecycleRulesDrawer';
import { RECORD_LABELS_LIFECYCLE_RULES } from '@/features/lifecycle-rules/lifecycleRulesContent';
import type { ColumnType, TableRowSelection } from 'antd/es/table/interface';
import PageTitle from '@/common/PageTitle/PageTitle';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import styles from './record-labels.module.scss';

import type { RecordLabel, RecordLabelStatus } from '@/features/record-labels/recordLabelsApi';
import {
  useListRecordLabelsQuery,
  useCreateRecordLabelMutation,
  useUpdateRecordLabelMutation,
  useArchiveRecordLabelMutation,
  useRestoreRecordLabelMutation,
  usePreflightPurgeRecordLabelMutation,
  usePurgeRecordLabelMutation,
  useBulkPurgeRecordLabelsMutation,
  isDuplicateNameConflict,
  extractRecordLabelErrorMessage,
} from '@/features/record-labels/recordLabelsApi';
import {
  CatalogLifecycleTabs,
  CatalogArchiveModal,
  CatalogRestoreModal,
  CatalogBatchConfirmModal,
  CatalogPermanentDeleteModal,
  CatalogPurgeFailuresModal,
  useCatalogPurgeFlow,
  useBatchActions,
  catalogBatchOptions,
  mixedSelectionWarning,
  type BatchActionValue,
  type CatalogLifecycleView,
} from '@/features/catalog-lifecycle';
import {
  useCanEditPage,
  useCanPerformLifecycleDelete,
} from '@/contexts/SuperAdminIdentityContext';

const PAGE_ID = 'record-labels';
const KIND = 'record-label' as const;

type RecordLabelRecord = {
  key: string;
  recordLabelId: string;
  recordLabelName: string;
  status: RecordLabelStatus;
  songCount: number;
  createdBySource: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
};

type RecordLabelEditorState = {
  name: string;
};

const EMPTY_EDITOR: RecordLabelEditorState = {
  name: '',
};

type DuplicateConfirmContext =
  | { mode: 'create'; payload: { name: string }; attemptedName: string }
  | {
      mode: 'edit';
      recordLabelId: string;
      payload: { name?: string };
      attemptedName: string;
    };

function toRecordLabelRecord(row: RecordLabel): RecordLabelRecord {
  return {
    key: row.recordLabelId,
    recordLabelId: row.recordLabelId,
    recordLabelName: row.name,
    status: row.status,
    songCount: row.activeSongCount ?? 0,
    createdBySource: row.createdBySource ?? '',
    createdAt: dayjs(row.createdAt).format('YYYY-MM-DD'),
    updatedAt: dayjs(row.updatedAt).format('YYYY-MM-DD'),
    deletedAt: row.deletedAt,
  };
}

function formatStatusLabel(status: RecordLabelStatus): string {
  return status === 'deleted' ? 'Archived' : 'Active';
}

/** Map wire status (`deleted`) to UI lifecycle (`archived`) for batch helpers. */
function toUiLifecycle(status: RecordLabelStatus): 'active' | 'archived' {
  return status === 'deleted' ? 'archived' : 'active';
}

function listStatusForView(view: CatalogLifecycleView): RecordLabelStatus | 'all' {
  if (view === 'archived') return 'deleted';
  return view;
}

function isEditorValid(state: RecordLabelEditorState): boolean {
  return state.name.trim().length > 0;
}

function findActiveDuplicateByName(
  rows: RecordLabelRecord[],
  name: string,
  excludeRecordLabelId?: string,
): RecordLabelRecord | undefined {
  const nameLower = name.trim().toLowerCase();
  if (nameLower.length === 0) return undefined;
  return rows.find(
    (row) =>
      row.status === 'active' &&
      row.recordLabelId !== excludeRecordLabelId &&
      row.recordLabelName.trim().toLowerCase() === nameLower,
  );
}

function mergeActiveDuplicateCheckRows(
  ...sources: RecordLabelRecord[][]
): RecordLabelRecord[] {
  const byId = new Map<string, RecordLabelRecord>();
  for (const rows of sources) {
    for (const row of rows) {
      if (row.status === 'active') {
        byId.set(row.recordLabelId, row);
      }
    }
  }
  return [...byId.values()];
}

function openDuplicateConfirm(
  context: DuplicateConfirmContext,
  setDuplicateConfirm: (value: DuplicateConfirmContext | null) => void,
  options?: {
    setAddModalOpen?: (open: boolean) => void;
    closeEditModal?: () => void;
  },
) {
  if (context.mode === 'create' && options?.setAddModalOpen) {
    options.setAddModalOpen(false);
  }
  if (context.mode === 'edit' && options?.closeEditModal) {
    options.closeEditModal();
  }
  setDuplicateConfirm(context);
}

const RecordLabels: React.FC = () => {
  const { message } = App.useApp();
  const canEdit = useCanEditPage(PAGE_ID);
  const canLifecycleDelete = useCanPerformLifecycleDelete();

  const [view, setView] = useState<CatalogLifecycleView>('active');
  const [lifecycleHelpOpen, setLifecycleHelpOpen] = useState(false);

  const listStatus = listStatusForView(view);

  const { data, isLoading, isFetching } = useListRecordLabelsQuery({
    status: listStatus,
    limit: 200,
  });
  const { data: activeData } = useListRecordLabelsQuery({
    status: 'active',
    limit: 200,
  });
  const { data: archivedData } = useListRecordLabelsQuery({
    status: 'deleted',
    limit: 200,
  });
  const [createRecordLabel, createState] = useCreateRecordLabelMutation();
  const [updateRecordLabel, updateState] = useUpdateRecordLabelMutation();
  const [archiveRecordLabel, archiveState] = useArchiveRecordLabelMutation();
  const [restoreRecordLabel, restoreState] = useRestoreRecordLabelMutation();
  const [preflightPurge] = usePreflightPurgeRecordLabelMutation();
  const [purgeRecordLabel] = usePurgeRecordLabelMutation();
  const [bulkPurge] = useBulkPurgeRecordLabelsMutation();

  const {
    selectedRowKeys,
    setSelectedRowKeys,
    batchAction,
    batchModalOpen,
    chooseBatchAction,
    resetBatchUi,
    clearBatchAction,
  } = useBatchActions();

  const purgeFlow = useCatalogPurgeFlow({
    kind: KIND,
    canLifecycle: canLifecycleDelete,
    message,
    preflight: (id) => preflightPurge({ recordLabelId: id }).unwrap(),
    purge: ({ id, name, reason }) =>
      purgeRecordLabel({
        recordLabelId: id,
        reason,
        confirmName: name,
        confirmRecordLabelId: id,
      }).unwrap(),
    bulkPurge: ({ ids, reason }) => bulkPurge({ ids, reason }).unwrap(),
    extractErrorMessage: extractRecordLabelErrorMessage,
    resetBatchUi,
  });

  const recordLabels: RecordLabelRecord[] = useMemo(
    () => (data?.recordLabels ?? []).map((row) => toRecordLabelRecord(row)),
    [data],
  );

  const activeRecordLabels: RecordLabelRecord[] = useMemo(
    () => (activeData?.recordLabels ?? []).map((row) => toRecordLabelRecord(row)),
    [activeData],
  );

  const duplicateCheckRows = useMemo(
    () => mergeActiveDuplicateCheckRows(activeRecordLabels, recordLabels),
    [activeRecordLabels, recordLabels],
  );

  const activeCount =
    activeData?.recordLabels != null ? activeData.recordLabels.length : undefined;
  const archivedCount =
    archivedData?.recordLabels != null ? archivedData.recordLabels.length : undefined;

  const filterOptions = useMemo(
    () => ({
      recordLabelNames: recordLabels
        .map((row) => row.recordLabelName)
        .sort((a, b) => a.localeCompare(b)),
    }),
    [recordLabels],
  );

  const [filters, setFilters] = useState({
    recordLabelName: undefined as string | undefined,
    createdAt: undefined as string | undefined,
    updatedAt: undefined as string | undefined,
  });
  const [resetCounter, setResetCounter] = useState(0);

  // Selection framework stays visible on every lifecycle tab (owner UX).
  const rowSelection: TableRowSelection<RecordLabelRecord> | undefined =
    canLifecycleDelete
      ? {
          selectedRowKeys,
          onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
        }
      : undefined;

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addEditor, setAddEditor] = useState<RecordLabelEditorState>(EMPTY_EDITOR);

  const [editingLabel, setEditingLabel] = useState<RecordLabelRecord | null>(null);
  const [editEditor, setEditEditor] = useState<RecordLabelEditorState>(EMPTY_EDITOR);
  const editModalOpen = editingLabel != null;

  const [viewingLabel, setViewingLabel] = useState<RecordLabelRecord | null>(null);
  const viewModalOpen = viewingLabel != null;

  const [archivingLabel, setArchivingLabel] = useState<RecordLabelRecord | null>(null);
  const archiveModalOpen = archivingLabel != null;

  const [restoringLabel, setRestoringLabel] = useState<RecordLabelRecord | null>(null);
  const restoreModalOpen = restoringLabel != null;

  const [duplicateConfirm, setDuplicateConfirm] = useState<DuplicateConfirmContext | null>(
    null,
  );
  const duplicateModalOpen = duplicateConfirm != null;

  const filteredRecordLabels = recordLabels.filter((label) => {
    return (
      (!filters.recordLabelName || label.recordLabelName === filters.recordLabelName) &&
      (!filters.createdAt || label.createdAt === filters.createdAt) &&
      (!filters.updatedAt || label.updatedAt === filters.updatedAt)
    );
  });

  const selectedRows = useMemo(
    () => filteredRecordLabels.filter((row) => selectedRowKeys.includes(row.key)),
    [filteredRecordLabels, selectedRowKeys],
  );

  const selectionLifecycle = useMemo(() => {
    if (selectedRows.length === 0) return 'none' as const;
    const statuses = new Set(selectedRows.map((row) => toUiLifecycle(row.status)));
    if (statuses.size > 1) return 'mixed' as const;
    return toUiLifecycle(selectedRows[0].status);
  }, [selectedRows]);

  // Status-scoped subsets of the current selection — mixed All-tab
  // selections run each batch action against its matching subset only
  // (owner-approved 2026-08-11, Song Library parity).
  const selectedActiveIds = useMemo(
    () =>
      selectedRows
        .filter((row) => toUiLifecycle(row.status) === 'active')
        .map((row) => row.key),
    [selectedRows],
  );
  const selectedArchivedIds = useMemo(
    () =>
      selectedRows
        .filter((row) => toUiLifecycle(row.status) === 'archived')
        .map((row) => row.key),
    [selectedRows],
  );

  const batchOptions = useMemo(
    () =>
      catalogBatchOptions(view, selectionLifecycle, {
        active: selectedActiveIds.length,
        archived: selectedArchivedIds.length,
      }),
    [view, selectionLifecycle, selectedActiveIds.length, selectedArchivedIds.length],
  );

  /** Ids the latched batch action will run against (scoped when mixed). */
  const batchTargetIds = useMemo(() => {
    if (selectionLifecycle === 'mixed') {
      if (batchAction === 'archive') return selectedActiveIds;
      if (batchAction === 'restore') return selectedArchivedIds;
      return [];
    }
    return selectedRowKeys.map(String);
  }, [selectionLifecycle, batchAction, selectedActiveIds, selectedArchivedIds, selectedRowKeys]);

  const switchView = (next: CatalogLifecycleView) => {
    setView(next);
    resetBatchUi();
  };

  const handleBatchAction = (value: BatchActionValue) => {
    if (!canLifecycleDelete) return;
    chooseBatchAction(value, selectionLifecycle, {
      onMixedSelection: () => {
        message.warning(mixedSelectionWarning('record labels'));
      },
      onDelete: (ids) => void purgeFlow.openBulk(ids),
      mixedScope: {
        activeIds: selectedActiveIds,
        archivedIds: selectedArchivedIds,
      },
    });
  };

  const handleConfirmBatch = async () => {
    if (!canLifecycleDelete || !batchAction) return;
    if (batchTargetIds.length === 0) return;
    let failures = 0;
    for (const key of batchTargetIds) {
      try {
        // eslint-disable-next-line no-await-in-loop
        if (batchAction === 'archive') {
          await archiveRecordLabel({ recordLabelId: String(key) }).unwrap();
        } else if (batchAction === 'restore') {
          await restoreRecordLabel({ recordLabelId: String(key) }).unwrap();
        }
      } catch {
        failures += 1;
      }
    }
    const succeeded = batchTargetIds.length - failures;
    if (batchAction === 'archive') {
      if (succeeded > 0) {
        message.success(
          `Archived ${succeeded} record label${succeeded === 1 ? '' : 's'}`,
        );
      }
      if (failures > 0) {
        message.error(`Failed to archive ${failures} record label(s)`);
      }
    } else if (batchAction === 'restore') {
      if (succeeded > 0) {
        message.success(
          `Restored ${succeeded} record label${succeeded === 1 ? '' : 's'}`,
        );
      }
      if (failures > 0) {
        message.error(`Failed to restore ${failures} record label(s)`);
      }
    }
    resetBatchUi();
  };

  const handleCancelBatch = () => {
    // Keep row selection; only clear the latched action (Song Library parity).
    clearBatchAction();
  };

  const handleFilterChange = (key: string, value: string | undefined) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setFilters({
      recordLabelName: undefined,
      createdAt: undefined,
      updatedAt: undefined,
    });
    setView('active');
    resetBatchUi();
    setResetCounter((prev) => prev + 1);
  };

  const handleCancelAdd = () => {
    setAddEditor(EMPTY_EDITOR);
    setAddModalOpen(false);
  };

  const openAdd = () => {
    if (!canEdit) return;
    setAddEditor(EMPTY_EDITOR);
    setAddModalOpen(true);
  };

  const submitCreate = async (allowDuplicateName = false) => {
    const trimmedName = addEditor.name.trim();
    if (trimmedName.length === 0) return;

    if (!allowDuplicateName && findActiveDuplicateByName(duplicateCheckRows, trimmedName)) {
      openDuplicateConfirm(
        {
          mode: 'create',
          payload: { name: trimmedName },
          attemptedName: trimmedName,
        },
        setDuplicateConfirm,
        { setAddModalOpen },
      );
      return;
    }

    try {
      const created = await createRecordLabel({
        name: trimmedName,
        ...(allowDuplicateName ? { allowDuplicateName: true } : {}),
      }).unwrap();
      message.success(`Created record label "${created.name}"`);
      setAddEditor(EMPTY_EDITOR);
      setAddModalOpen(false);
      setDuplicateConfirm(null);
    } catch (err: unknown) {
      if (!allowDuplicateName && isDuplicateNameConflict(err)) {
        openDuplicateConfirm(
          {
            mode: 'create',
            payload: { name: trimmedName },
            attemptedName: trimmedName,
          },
          setDuplicateConfirm,
          { setAddModalOpen },
        );
        return;
      }
      const msg = extractRecordLabelErrorMessage(err);
      if (msg) message.error(msg);
    }
  };

  const openEdit = (record: RecordLabelRecord) => {
    if (!canEdit || record.status !== 'active') return;
    setEditingLabel(record);
    setEditEditor({
      name: record.recordLabelName,
    });
  };

  const openView = (record: RecordLabelRecord) => {
    setViewingLabel(record);
  };

  const handleCancelEdit = () => {
    setEditEditor(EMPTY_EDITOR);
    setEditingLabel(null);
  };

  const submitUpdate = async (allowDuplicateName = false) => {
    if (!editingLabel) return;
    const trimmedName = editEditor.name.trim();
    if (trimmedName.length === 0) return;

    if (trimmedName === editingLabel.recordLabelName) {
      handleCancelEdit();
      return;
    }

    if (
      !allowDuplicateName &&
      findActiveDuplicateByName(
        duplicateCheckRows,
        trimmedName,
        editingLabel.recordLabelId,
      )
    ) {
      openDuplicateConfirm(
        {
          mode: 'edit',
          recordLabelId: editingLabel.recordLabelId,
          payload: { name: trimmedName },
          attemptedName: trimmedName,
        },
        setDuplicateConfirm,
        { closeEditModal: handleCancelEdit },
      );
      return;
    }

    try {
      const updated = await updateRecordLabel({
        recordLabelId: editingLabel.recordLabelId,
        name: trimmedName,
        ...(allowDuplicateName ? { allowDuplicateName: true } : {}),
      }).unwrap();
      message.success(`Updated record label "${updated.name}"`);
      setEditEditor(EMPTY_EDITOR);
      setEditingLabel(null);
      setDuplicateConfirm(null);
    } catch (err: unknown) {
      if (!allowDuplicateName && isDuplicateNameConflict(err)) {
        openDuplicateConfirm(
          {
            mode: 'edit',
            recordLabelId: editingLabel.recordLabelId,
            payload: { name: trimmedName },
            attemptedName: trimmedName,
          },
          setDuplicateConfirm,
          { closeEditModal: handleCancelEdit },
        );
        return;
      }
      const msg = extractRecordLabelErrorMessage(err);
      if (msg) message.error(msg);
    }
  };

  const handleCancelArchive = () => {
    setArchivingLabel(null);
  };

  const submitArchive = async () => {
    if (!archivingLabel) return;
    try {
      await archiveRecordLabel({ recordLabelId: archivingLabel.recordLabelId }).unwrap();
      message.success('Record label archived — moved to Archived Record Labels');
      setArchivingLabel(null);
    } catch (err: unknown) {
      const msg = extractRecordLabelErrorMessage(err);
      message.error(msg || 'Failed to archive record label');
    }
  };

  const handleCancelRestore = () => {
    setRestoringLabel(null);
  };

  const submitRestore = async () => {
    if (!restoringLabel) return;
    try {
      await restoreRecordLabel({ recordLabelId: restoringLabel.recordLabelId }).unwrap();
      message.success('Record label restored — moved to Active Record Labels');
      setRestoringLabel(null);
    } catch (err: unknown) {
      const msg = extractRecordLabelErrorMessage(err);
      message.error(msg || 'Failed to restore record label');
    }
  };

  const handleCancelDuplicate = () => {
    const pending = duplicateConfirm;
    setDuplicateConfirm(null);
    if (pending?.mode === 'create') {
      setAddModalOpen(true);
      setAddEditor((prev) => ({
        ...prev,
        name: pending.attemptedName,
      }));
    } else if (pending?.mode === 'edit') {
      const row =
        duplicateCheckRows.find((label) => label.recordLabelId === pending.recordLabelId) ??
        recordLabels.find((label) => label.recordLabelId === pending.recordLabelId);
      if (row) {
        setEditingLabel(row);
      }
      setEditEditor({
        name: pending.attemptedName,
      });
    }
  };

  const submitDuplicateOverride = async () => {
    if (!duplicateConfirm) return;
    if (duplicateConfirm.mode === 'create') {
      try {
        const created = await createRecordLabel({
          ...duplicateConfirm.payload,
          allowDuplicateName: true,
        }).unwrap();
        message.success(`Created record label "${created.name}"`);
        setAddEditor(EMPTY_EDITOR);
        setAddModalOpen(false);
        setDuplicateConfirm(null);
      } catch (err: unknown) {
        const msg = extractRecordLabelErrorMessage(err);
        if (msg) message.error(msg);
      }
      return;
    }

    try {
      const updated = await updateRecordLabel({
        recordLabelId: duplicateConfirm.recordLabelId,
        ...duplicateConfirm.payload,
        allowDuplicateName: true,
      }).unwrap();
      message.success(`Updated record label "${updated.name}"`);
      setEditEditor(EMPTY_EDITOR);
      setEditingLabel(null);
      setDuplicateConfirm(null);
    } catch (err: unknown) {
      const msg = extractRecordLabelErrorMessage(err);
      if (msg) message.error(msg);
    }
  };

  const columns: ColumnType<RecordLabelRecord>[] = [
    {
      title: 'Record Label Name',
      dataIndex: 'recordLabelName',
      sorter: (a, b) => a.recordLabelName.localeCompare(b.recordLabelName),
      width: 260,
      render: (_: unknown, record: RecordLabelRecord) => (
        <span>
          {record.recordLabelName}
          {record.createdBySource === 'pipeline' && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              Auto-created
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: 'Songs',
      dataIndex: 'songCount',
      sorter: (a, b) => a.songCount - b.songCount,
      width: 90,
      render: (count: number, record: RecordLabelRecord) => (
        <Link
          to={`/songs?recordLabelId=${encodeURIComponent(record.recordLabelId)}&status=active`}
        >
          {count}
        </Link>
      ),
    },
    ...(view !== 'active'
      ? [
          {
            title: 'Status',
            dataIndex: 'status',
            width: 110,
            render: (status: RecordLabelStatus) => formatStatusLabel(status),
          } as ColumnType<RecordLabelRecord>,
        ]
      : []),
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      width: 140,
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      sorter: (a, b) => dayjs(b.updatedAt).unix() - dayjs(a.updatedAt).unix(),
      width: 140,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 280,
      render: (_: unknown, record: RecordLabelRecord) => {
        const showArchivedLifecycleActions =
          canLifecycleDelete && record.status === 'deleted';

        return (
          <span>
            <a
              style={{ marginRight: 12, cursor: 'pointer', color: '#1677ff' }}
              onClick={() => openView(record)}
            >
              View
            </a>
            {canEdit && record.status === 'active' ? (
              <a
                style={{ marginRight: 12, cursor: 'pointer', color: '#1677ff' }}
                onClick={() => openEdit(record)}
              >
                Edit
              </a>
            ) : null}
            {canLifecycleDelete && record.status === 'active' ? (
              <a
                style={{ marginRight: 12, color: '#e21e26', cursor: 'pointer' }}
                onClick={() => setArchivingLabel(record)}
              >
                Archive
              </a>
            ) : null}
            {showArchivedLifecycleActions ? (
              <>
                <a
                  style={{ marginRight: 12, color: '#1677ff', cursor: 'pointer' }}
                  onClick={() => setRestoringLabel(record)}
                >
                  Restore
                </a>
                <a
                  style={{ color: '#e21e26', cursor: 'pointer' }}
                  onClick={() =>
                    void purgeFlow.openSingle(record.recordLabelId, record.recordLabelName)
                  }
                >
                  Purge
                </a>
              </>
            ) : null}
          </span>
        );
      },
    },
  ];

  const showSecondFilterRow = canEdit || canLifecycleDelete;

  return (
    <Container fluid>
      <Card style={{ borderRadius: 12 }}>
        <Card.Body>
          <Card.Title style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <PageTitle title="Record Labels" />
              <LifecycleRulesInfoButton onClick={() => setLifecycleHelpOpen(true)} />
            </span>
            <Button
              onClick={handleReset}
              style={{
                background: 'white',
                color: '#333',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: '500',
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>

          <CatalogLifecycleTabs
            view={view}
            onChange={switchView}
            activeCount={activeCount}
            archivedCount={archivedCount}
            testId="record-labels-lifecycle-tabs"
          />

          <Form layout="vertical" className={styles.form}>
            <BootstrapRow>
              <BootstrapCol>
                <BootstrapRow className="mb-2">
                  <BootstrapCol>
                    <BootstrapRow>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Search by record label name"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`recordLabelName-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any record label name"
                            value={filters.recordLabelName}
                            onChange={(value) => handleFilterChange('recordLabelName', value)}
                            options={filterOptions.recordLabelNames.map((name) => ({
                              label: name,
                              value: name,
                            }))}
                          />
                        </FormItem>
                      </BootstrapCol>
                      <BootstrapCol md={4}>
                        <FormItem
                          label="Created at"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <DatePicker
                            key={`createdAt-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.dateField}`}
                            placeholder="Created at"
                            value={filters.createdAt ? dayjs(filters.createdAt) : undefined}
                            onChange={(date) =>
                              handleFilterChange(
                                'createdAt',
                                date ? date.format('YYYY-MM-DD') : undefined,
                              )
                            }
                          />
                        </FormItem>
                      </BootstrapCol>
                      {!showSecondFilterRow ? (
                        <BootstrapCol md={4}>
                          <FormItem
                            label="Updated at"
                            className="customSelectWrapper"
                            layout="vertical"
                          >
                            <DatePicker
                              key={`updatedAt-reset-${resetCounter}`}
                              allowClear
                              className={`customSelect ${styles.dateField}`}
                              placeholder="Updated at"
                              value={filters.updatedAt ? dayjs(filters.updatedAt) : undefined}
                              onChange={(date) =>
                                handleFilterChange(
                                  'updatedAt',
                                  date ? date.format('YYYY-MM-DD') : undefined,
                                )
                              }
                            />
                          </FormItem>
                        </BootstrapCol>
                      ) : null}
                    </BootstrapRow>
                  </BootstrapCol>
                </BootstrapRow>
                {showSecondFilterRow ? (
                  <BootstrapRow className="mb-2">
                    <BootstrapCol>
                      <BootstrapRow>
                        <BootstrapCol md={4}>
                          <FormItem
                            label="Updated at"
                            className="customSelectWrapper"
                            layout="vertical"
                          >
                            <DatePicker
                              key={`updatedAt-reset-${resetCounter}`}
                              allowClear
                              className={`customSelect ${styles.dateField}`}
                              placeholder="Updated at"
                              value={filters.updatedAt ? dayjs(filters.updatedAt) : undefined}
                              onChange={(date) =>
                                handleFilterChange(
                                  'updatedAt',
                                  date ? date.format('YYYY-MM-DD') : undefined,
                                )
                              }
                            />
                          </FormItem>
                        </BootstrapCol>
                        {canLifecycleDelete ? (
                          <BootstrapCol md={4}>
                            <FormItem
                              label="Batch Actions"
                              className="customSelectWrapper"
                              layout="vertical"
                            >
                              <Select<BatchActionValue>
                                key={`batchActions-reset-${resetCounter}`}
                                className={`customSelect ${styles.selectField}`}
                                placeholder="Batch Actions"
                                value={batchAction}
                                onChange={handleBatchAction}
                                disabled={
                                  selectedRowKeys.length === 0 ||
                                  batchOptions.length === 0
                                }
                                options={batchOptions}
                              />
                            </FormItem>
                          </BootstrapCol>
                        ) : null}
                        {canEdit ? (
                          <BootstrapCol
                            md={canLifecycleDelete ? 4 : 8}
                            className="d-flex align-items-center mt-2 mt-md-0"
                          >
                            <Button
                              className="w-100"
                              style={{
                                background: 'white',
                                color: '#e21e26',
                                border: '1px solid #e21e26',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                justifyContent: 'center',
                                height: '50px',
                                marginTop: '8px',
                              }}
                              onClick={openAdd}
                            >
                              <PlusOutlined style={{ fontSize: 16 }} />
                              Add New Record Label
                            </Button>
                          </BootstrapCol>
                        ) : null}
                      </BootstrapRow>
                    </BootstrapCol>
                  </BootstrapRow>
                ) : null}
              </BootstrapCol>
            </BootstrapRow>
          </Form>

          <Table
            className={`legacy-row-height-table ${styles.table}`}
            rowSelection={rowSelection}
            columns={columns}
            dataSource={filteredRecordLabels}
            pagination={{ pageSize: 10, showQuickJumper: true }}
            rowKey="key"
            bordered
            scroll={{ x: true }}
            loading={{
              spinning: isLoading || isFetching,
              indicator: <LoadingOutlined style={{ fontSize: 24 }} spin />,
            }}
          />
        </Card.Body>
      </Card>

      <BootstrapModal show={addModalOpen} onHide={handleCancelAdd} size="lg">
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>Add New Record Label</BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapForm
          onSubmit={(event) => {
            event.preventDefault();
            void submitCreate(false);
          }}
        >
          <BootstrapModal.Body>
            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>Record Label Name *</BootstrapForm.Label>
              <BootstrapForm.Control
                type="text"
                placeholder="Enter record label name"
                value={addEditor.name}
                maxLength={256}
                onChange={(e) =>
                  setAddEditor((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </BootstrapForm.Group>
          </BootstrapModal.Body>
          <BootstrapModal.Footer>
            <Button type="button" variant="secondary" onClick={handleCancelAdd}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={
                !isEditorValid(addEditor) ||
                createState.isLoading ||
                activeData == null
              }
              style={{ background: '#e21e26', border: 'none' }}
            >
              {createState.isLoading ? 'Creating…' : 'Create Record Label'}
            </Button>
          </BootstrapModal.Footer>
        </BootstrapForm>
      </BootstrapModal>

      <BootstrapModal show={editModalOpen} onHide={handleCancelEdit} size="lg">
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>Edit Record Label</BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapForm
          onSubmit={(event) => {
            event.preventDefault();
            void submitUpdate(false);
          }}
        >
          <BootstrapModal.Body>
            <BootstrapForm.Group className="mb-3">
              <BootstrapForm.Label>Record Label Name *</BootstrapForm.Label>
              <BootstrapForm.Control
                type="text"
                placeholder="Enter record label name"
                value={editEditor.name}
                maxLength={256}
                onChange={(e) =>
                  setEditEditor((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </BootstrapForm.Group>
          </BootstrapModal.Body>
          <BootstrapModal.Footer>
            <Button type="button" variant="secondary" onClick={handleCancelEdit}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={
                !isEditorValid(editEditor) ||
                updateState.isLoading ||
                activeData == null
              }
              style={{ background: '#e21e26', border: 'none' }}
            >
              {updateState.isLoading ? 'Saving…' : 'Save Changes'}
            </Button>
          </BootstrapModal.Footer>
        </BootstrapForm>
      </BootstrapModal>

      <BootstrapModal show={viewModalOpen} onHide={() => setViewingLabel(null)} size="lg">
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>Record Label Details</BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          {viewingLabel ? (
            <BootstrapForm>
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Record Label Name</BootstrapForm.Label>
                <BootstrapForm.Control
                  type="text"
                  readOnly
                  plaintext
                  value={viewingLabel.recordLabelName}
                />
              </BootstrapForm.Group>
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Status</BootstrapForm.Label>
                <BootstrapForm.Control
                  type="text"
                  readOnly
                  plaintext
                  value={formatStatusLabel(viewingLabel.status)}
                />
              </BootstrapForm.Group>
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Created At</BootstrapForm.Label>
                <BootstrapForm.Control type="text" readOnly plaintext value={viewingLabel.createdAt} />
              </BootstrapForm.Group>
              <BootstrapForm.Group className="mb-3">
                <BootstrapForm.Label>Updated At</BootstrapForm.Label>
                <BootstrapForm.Control type="text" readOnly plaintext value={viewingLabel.updatedAt} />
              </BootstrapForm.Group>
            </BootstrapForm>
          ) : null}
        </BootstrapModal.Body>
        <BootstrapModal.Footer>
          <Button variant="secondary" onClick={() => setViewingLabel(null)}>
            Close
          </Button>
        </BootstrapModal.Footer>
      </BootstrapModal>

      <CatalogArchiveModal
        kind={KIND}
        open={archiveModalOpen}
        name={archivingLabel?.recordLabelName}
        loading={archiveState.isLoading}
        onCancel={handleCancelArchive}
        onConfirm={() => void submitArchive()}
      />

      <CatalogRestoreModal
        kind={KIND}
        open={restoreModalOpen}
        name={restoringLabel?.recordLabelName}
        loading={restoreState.isLoading}
        onCancel={handleCancelRestore}
        onConfirm={() => void submitRestore()}
      />

      <CatalogBatchConfirmModal
        kind={KIND}
        open={batchModalOpen}
        action={batchAction === 'archive' || batchAction === 'restore' ? batchAction : null}
        selectedCount={batchTargetIds.length}
        mixedScoped={selectionLifecycle === 'mixed'}
        loading={archiveState.isLoading || restoreState.isLoading}
        onCancel={handleCancelBatch}
        onConfirm={() => void handleConfirmBatch()}
      />

      <CatalogPermanentDeleteModal {...purgeFlow.modalProps} />
      <CatalogPurgeFailuresModal {...purgeFlow.failuresProps} />

      <BootstrapModal show={duplicateModalOpen} onHide={handleCancelDuplicate} size="lg">
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>Record label already exists</BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          {duplicateConfirm ? (
            <p className="mb-0">
              A record label named &quot;{duplicateConfirm.attemptedName}&quot; already exists.
            </p>
          ) : null}
        </BootstrapModal.Body>
        <BootstrapModal.Footer>
          <Button type="button" variant="secondary" onClick={handleCancelDuplicate}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={createState.isLoading || updateState.isLoading}
            onClick={() => void submitDuplicateOverride()}
            style={{ background: '#e21e26', border: 'none' }}
          >
            {createState.isLoading || updateState.isLoading
              ? duplicateConfirm?.mode === 'edit'
                ? 'Saving…'
                : 'Creating…'
              : duplicateConfirm?.mode === 'edit'
                ? 'Save Anyway'
                : 'Create Anyway'}
          </Button>
        </BootstrapModal.Footer>
      </BootstrapModal>

      <LifecycleRulesDrawer
        content={RECORD_LABELS_LIFECYCLE_RULES}
        open={lifecycleHelpOpen}
        onClose={() => setLifecycleHelpOpen(false)}
      />
    </Container>
  );
};

export default withAuthenticationRequired(RecordLabels);
